import { createRoot } from "react-dom/client";
import App from "./App";
import { consumeInviteAccept } from "../main/events/invite-events";
import { captureEntry } from "./lib/entry-attribution";
// KaTeX rules as their own sheet rather than an @import inside styles.css:
// Tailwind v4 owns that file's pipeline, and the manual content that needs
// KaTeX lives inside the shadow root where document CSS can't reach. The
// FONTS the rules reference only register at document level — lib/math-tex.ts
// links the same file into document.head for that, on first formula.
import katexStyles from "katex/dist/katex.min.css?inline";
import styles from "./styles.css?inline";

// The app renders inside a shadow root so the site's global CSS (Bootstrap,
// helpers like .hidden) cannot affect it, and Lume's Tailwind styles
// (adopted below, never added to the document) cannot leak out.
const previewContainer = document.getElementById("lume-manual-preview-root");
const container = document.getElementById("lume-root") ?? previewContainer;

if (container) {
  if (container !== previewContainer) {
    captureEntry();
    consumeInviteAccept();
  }

  // Tailwind v4 registers its --tw-* variable defaults with @property, which
  // browsers only honor at document level — inside a shadow root the rules
  // are ignored, breaking var()-based utilities (gradients render
  // transparent, shadows/rings vanish). Hoist the registrations into the
  // document; they're global by nature and nothing outside uses --tw-*.
  const propertyRules = styles.match(/@property[\s\S]*?\}/g);
  if (propertyRules && !document.getElementById("lume-css-properties")) {
    const style = document.createElement("style");
    style.id = "lume-css-properties";
    style.textContent = propertyRules.join("\n");
    document.head.appendChild(style);
  }

  const shadow = container.attachShadow({ mode: "open" });

  const sheet = new CSSStyleSheet();
  sheet.replaceSync(styles);

  const katexSheet = new CSSStyleSheet();
  katexSheet.replaceSync(katexStyles);

  // KaTeX after Tailwind, so its element rules win ties against preflight
  // (preflight's `svg { display: block }` would break stretchy delimiters).
  shadow.adoptedStyleSheets = [sheet, katexSheet];

  const mount = document.createElement("div");
  shadow.appendChild(mount);

  if (container === previewContainer) {
    const dataEl = document.getElementById("lume-manual-preview-data");
    const data = JSON.parse(dataEl?.textContent || "{}");
    import("./components/caderno/ManualPreview").then(({ default: ManualPreview }) => {
      createRoot(mount).render(<ManualPreview data={data} />);
    });
  } else {
    createRoot(mount).render(<App />);
  }
}
